export type SSEHandler = (data: Record<string, unknown>) => void;

export interface SSECallbacks {
  onConnectionLost: (reconnectFn: () => void) => void;
  onReconnected: () => void;
  onDeactivated: () => void; // 403 from auth probe
}

// Must match HEARTBEAT_INTERVAL in api/sse.py (30s), with buffer
const HEARTBEAT_INTERVAL_MS = 30_000;

type SSEState =
  | 'CONNECTING'
  | 'CONNECTED'
  | 'ERROR_BACKOFF'
  | 'PROBING'
  | 'WAITING'
  | 'STOPPED'
  | 'CONNECTION_LOST';

export class SSEClient {
  private _onMessage: SSEHandler;
  private _callbacks: SSECallbacks;

  // Nicht 'CONNECTING' — das ist genau der Wert, den connect() als „läuft schon" abfängt (siehe
  // dort). Mit 'CONNECTING' als Startwert war der allererste connect()-Aufruf immer ein No-op:
  // die EventSource wurde nie geöffnet, ohne Fehler, ohne Reconnect-Versuch. Betraf jedes
  // SSE-Feature der App (Publish-Toasts, Vault-Analyse, Leadgen-Präsenz, Skript-Live-Abgleich).
  private _state: SSEState = 'STOPPED';
  get state(): SSEState { return this._state; }

  private _eventSource: EventSource | null = null;
  private _errorCount = 0;
  private _cycleCount = 0;
  isClosed = false;
  private _stopped = false;
  private _probeInFlight = false;
  private _probeForReconnectInFlight = false;

  private _lastEventTime = Date.now();
  private _staleCallbacks: Array<{ callback: () => void; heartbeatCount: number; fired: boolean }> = [];
  private _watchdogInterval: ReturnType<typeof setInterval> | null = null;

  constructor(onMessage: SSEHandler, callbacks: SSECallbacks) {
    this._onMessage = onMessage;
    this._callbacks = callbacks;
  }

  connect(): void {
    if (this.isClosed || this._stopped) return;
    if (this._state === 'CONNECTING' || this._state === 'CONNECTED') return;

    const jwt = localStorage.getItem('token');
    if (!jwt) {
      this._stopped = true;
      window.location.href = '/login';
      return;
    }

    // Close any existing EventSource before creating a new one
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = null;
    }

    this._state = 'CONNECTING';
    const url = `/api/events?token=${encodeURIComponent(jwt)}`;
    const es = new EventSource(url);
    this._eventSource = es;

    es.onopen = () => {
      if (this.isClosed) return;
      this._state = 'CONNECTED';
      this._errorCount = 0;
      if (this._cycleCount > 0) {
        this._callbacks.onReconnected();
        this._cycleCount = 0;
      }
    };

    es.onmessage = (event) => {
      if (this.isClosed) return;
      this._lastEventTime = Date.now();
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'heartbeat') return;
        this._onMessage(data);
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      if (this.isClosed) return;
      es.close();
      this._eventSource = null;
      this._errorCount += 1;

      if (this._errorCount < 3) {
        this._state = 'ERROR_BACKOFF';
        const delay = Math.min(Math.pow(2, this._errorCount - 1) * 1000, 30_000);
        setTimeout(() => {
          if (this.isClosed || this._stopped) return;
          this.connect();
        }, delay);
      } else {
        this._probe();
      }
    };

    // Start watchdog on first connect
    if (this._watchdogInterval === null) {
      this._watchdogInterval = setInterval(() => {
        if (this.isClosed) return;
        const elapsed = Date.now() - this._lastEventTime;
        const missedHeartbeats = Math.floor(elapsed / HEARTBEAT_INTERVAL_MS);
        for (const entry of this._staleCallbacks) {
          if (!entry.fired && missedHeartbeats >= entry.heartbeatCount) {
            entry.fired = true;
            entry.callback();
          }
        }
      }, HEARTBEAT_INTERVAL_MS);
    }
  }

  reconnect(): void {
    if (this.isClosed) return;
    // Probe auth first, then connect or redirect
    this._probeForReconnect();
  }

  close(): void {
    this.isClosed = true;
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = null;
    }
    if (this._watchdogInterval !== null) {
      clearInterval(this._watchdogInterval);
      this._watchdogInterval = null;
    }
  }

  onStale(callback: () => void, opts: { heartbeatCount: number }): () => void {
    const entry = { callback, heartbeatCount: opts.heartbeatCount, fired: false };
    this._staleCallbacks.push(entry);
    return () => {
      const idx = this._staleCallbacks.indexOf(entry);
      if (idx !== -1) this._staleCallbacks.splice(idx, 1);
    };
  }

  private async _probe(): Promise<void> {
    if (this.isClosed || this._stopped) return;
    if (this._probeInFlight) return;
    this._probeInFlight = true;
    this._state = 'PROBING';

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        this._stopped = true;
        window.location.href = '/login';
        return;
      }

      let status: number | null = null;
      try {
        const res = await fetch('/api/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        status = res.status;
      } catch {
        // Network error: server was down, treat as auth OK
        status = null;
      }

      if (this.isClosed || this._stopped) return;

      if (status === 401) {
        this._stopped = true;
        this._state = 'STOPPED';
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      if (status === 403) {
        this._stopped = true;
        this._state = 'STOPPED';
        localStorage.removeItem('token');
        this._callbacks.onDeactivated();
        window.location.href = '/login';
        return;
      }

      // 200 or network error: server was down, check token change
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        this._stopped = true;
        this._state = 'STOPPED';
        window.location.href = '/login';
        return;
      }

      if (currentToken !== token) {
        // Token changed: fresh login, reset and connect immediately
        this._errorCount = 0;
        this._cycleCount = 0;
        this.connect();
        return;
      }

      // Same token: increment cycle count
      this._cycleCount += 1;

      if (this._cycleCount >= 3) {
        this._state = 'CONNECTION_LOST';
        this._callbacks.onConnectionLost(() => this.reconnect());
        return;
      }

      // Wait 30s before retrying
      this._state = 'WAITING';
      setTimeout(() => {
        if (this.isClosed || this._stopped) return;
        this.connect();
      }, 30_000);
    } finally {
      this._probeInFlight = false;
    }
  }

  private async _probeForReconnect(): Promise<void> {
    if (this.isClosed || this._stopped) return;
    if (this._probeForReconnectInFlight) return;
    this._probeForReconnectInFlight = true;
    this._state = 'PROBING';

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        this._stopped = true;
        this._state = 'STOPPED';
        window.location.href = '/login';
        return;
      }

      let status: number | null = null;
      try {
        const res = await fetch('/api/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        status = res.status;
      } catch {
        status = null;
      }

      if (this.isClosed || this._stopped) return;

      if (status === 401 || status === 403) {
        this._stopped = true;
        this._state = 'STOPPED';
        localStorage.removeItem('token');
        if (status === 403) this._callbacks.onDeactivated();
        window.location.href = '/login';
        return;
      }

      // Auth OK: reset counters and connect; cycleCount reset deferred to onopen
      // so that onopen's cycleCount > 0 check correctly fires onReconnected()
      this._errorCount = 0;
      this._stopped = false;
      this.connect();
    } finally {
      this._probeForReconnectInFlight = false;
    }
  }
}

export function createSSEClient(onMessage: SSEHandler, callbacks: SSECallbacks): SSEClient {
  return new SSEClient(onMessage, callbacks);
}
